/* © NXAPP Limited */
import { useEffect, useState } from 'react'
import { CheckCircle, Mail, Loader2 } from 'lucide-react'

interface Option { id: string; title: string; description: string }

export default function SubscribePage() {
  const [options, setOptions] = useState<Option[] | null>(null)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [company, setCompany] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')
  const [msg, setMsg] = useState('')
  const [logoOk, setLogoOk] = useState(true)

  useEffect(() => {
    fetch('/api/public/subscribe-options')
      .then(r => r.json())
      .then((d: { options?: Option[] }) => {
        const opts = d.options ?? []
        setOptions(opts)
        setPicked(new Set(opts.map(o => o.id)))   // default: all ticked
      })
      .catch(() => setOptions([]))
  }, [])

  const toggle = (id: string) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })

  const submit = () => {
    setMsg('')
    if (!firstName.trim() || !lastName.trim()) { setMsg('Please enter your first and last name.'); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setMsg('Please enter a valid email.'); return }
    if (picked.size === 0) { setMsg('Choose at least one.'); return }
    setState('sending')
    fetch('/api/public/subscribe', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, first_name: firstName, last_name: lastName, company, campaignIds: [...picked] }),
    })
      .then(r => r.json())
      .then((d: { ok?: boolean; error?: string; subscribed?: string[] }) => {
        if (d.error) { setState('error'); setMsg(d.error) }
        else { setState('done'); setMsg((d.subscribed ?? []).join(' · ')) }
      })
      .catch(() => { setState('error'); setMsg('Something went wrong — please try again.') })
  }

  return (
    <div className="min-h-screen bg-[#0f1115] text-gray-200 flex flex-col items-center px-4 py-12">
      <div className="w-full max-w-xl">
        {/* Brand */}
        <div className="text-center mb-8">
          {logoOk && <img src="/logo.png?v=1" alt="Asif Sarangi" onError={() => setLogoOk(false)} className="h-16 mx-auto mb-4 object-contain" />}
          <p className="text-[11px] tracking-[0.3em] text-amber-400/80 uppercase">asifsarangi.com · AS6 Agency</p>
          <h1 className="mt-2 text-2xl sm:text-3xl font-semibold text-white">Subscribe</h1>
          <p className="mt-2 text-sm text-gray-400">Tech, cyber &amp; AI intelligence for busy founders — straight to your inbox. No spam, unsubscribe any time.</p>
        </div>

        {state === 'done' ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto" />
            <h2 className="mt-3 text-lg font-semibold text-white">You’re in.</h2>
            <p className="mt-1 text-sm text-gray-400">Subscribed to: {msg || 'your selection'}. Watch your inbox.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {options === null ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>
            ) : options.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-10">No newsletters are open for subscription right now.</p>
            ) : (
              options.map(o => (
                <label key={o.id} className={`block rounded-xl border p-4 cursor-pointer transition-colors ${picked.has(o.id) ? 'border-amber-400/50 bg-amber-400/5' : 'border-gray-700 bg-white/[0.02] hover:border-gray-600'}`}>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={picked.has(o.id)} onChange={() => toggle(o.id)} className="mt-1 accent-amber-400 w-4 h-4" />
                    <div>
                      <p className="font-semibold text-white">{o.title}</p>
                      {o.description && <p className="mt-1 text-sm text-gray-400 leading-relaxed">{o.description}</p>}
                    </div>
                  </div>
                </label>
              ))
            )}

            {options && options.length > 0 && (
              <div className="rounded-xl border border-gray-700 bg-white/[0.02] p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name *"
                    className="w-full rounded-lg bg-[#0b0d11] border border-gray-700 px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:border-amber-400/50 outline-none" />
                  <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name *"
                    className="w-full rounded-lg bg-[#0b0d11] border border-gray-700 px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:border-amber-400/50 outline-none" />
                </div>
                <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company (optional)"
                  className="w-full rounded-lg bg-[#0b0d11] border border-gray-700 px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:border-amber-400/50 outline-none" />
                <div className="relative">
                  <Mail className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@example.com *"
                    className="w-full rounded-lg bg-[#0b0d11] border border-gray-700 pl-9 pr-3 py-2.5 text-sm text-white placeholder-gray-500 focus:border-amber-400/50 outline-none" />
                </div>
                {msg && <p className="text-xs text-amber-400">{msg}</p>}
                <button onClick={submit} disabled={state === 'sending'}
                  className="w-full rounded-lg bg-amber-400 hover:bg-amber-300 text-[#0f1115] font-semibold py-2.5 text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                  {state === 'sending' ? <><Loader2 className="w-4 h-4 animate-spin" /> Subscribing…</> : 'Subscribe'}
                </button>
                <p className="text-[11px] text-gray-500 text-center">By subscribing you agree to receive these emails. You can unsubscribe from any email in one click.</p>
              </div>
            )}
          </div>
        )}

        <p className="mt-8 text-center text-[11px] text-gray-600">© {'{year}'.replace('{year}', String(new Date().getFullYear()))} Asif Sarangi · asifsarangi.com</p>
      </div>
    </div>
  )
}
